import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Users, PlaneTakeoff, UserCheck, Check, Loader2, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CsvImportPanel } from '../components/onboarding/CsvImportPanel';
import { TeamInviteStep } from '../components/onboarding/TeamInviteStep';
import { ContractModuleButton } from '../components/ModuleUpsell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = [
    { n: 1, label: 'Aerolínea', icon: Building2 },
    { n: 2, label: 'Equipo', icon: Users },
    { n: 3, label: 'Flota y rutas', icon: PlaneTakeoff },
    { n: 4, label: 'Tripulación', icon: UserCheck },
];
const FIELDS = [
    ['name', 'Nombre comercial'], ['iata_code', 'Código IATA (2)'], ['icao_code', 'Código ICAO (3)'],
    ['country', 'País'], ['timezone', 'Zona horaria'], ['base_airport', 'Aeropuerto base (ICAO)'], ['contact_phone', 'Teléfono de contacto'],
];

const Stepper = ({ step, setStep }) => (
    <ol className="flex flex-wrap gap-2 mb-8" data-testid="onboarding-stepper">
        {STEPS.map(({ n, label, icon: Icon }) => (
            <li key={n}>
                <button onClick={() => setStep(n)} data-testid={`onboarding-step-${n}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm transition-colors ${n === step ? 'border-amber-500 bg-amber-500/10 text-amber-500' : n < step ? 'border-emerald-500/40 text-emerald-500' : 'border-border text-muted-foreground'}`}>
                    {n < step ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}{n}. {label}
                </button>
            </li>
        ))}
    </ol>
);

const AirlineStep = ({ airline, setAirline }) => (
    <div className="grid md:grid-cols-2 gap-4" data-testid="airline-step">
        {FIELDS.map(([k, l]) => (
            <div key={k} className="space-y-1">
                <Label>{l}</Label>
                <Input value={airline[k] || ''} onChange={(e) => setAirline({ ...airline, [k]: e.target.value })} data-testid={`airline-${k}`} />
            </div>
        ))}
    </div>
);

export default function OnboardingPage() {
    const navigate = useNavigate();
    const [state, setState] = useState(null);
    const [error, setError] = useState(null);
    const [step, setStep] = useState(1);
    const [airline, setAirline] = useState({});
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/onboarding`);
            setState(r.data);
            setAirline((a) => (Object.keys(a).length ? a : r.data.airline));
            setError(null);
        } catch (e) {
            setError(e.response?.data?.detail || 'No se pudo cargar el asistente');
        }
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { if (state && !state.completed && state.step > 1) setStep(state.step); }, [state?.completed]); // eslint-disable-line react-hooks/exhaustive-deps

    const saveProgress = async (next, completed = false) => {
        await axios.put(`${API}/onboarding`, { step: next, completed });
    };

    const goNext = async () => {
        setSaving(true);
        try {
            if (step === 1) {
                await axios.put(`${API}/onboarding/airline`, airline);
                toast.success('Datos de la aerolínea guardados');
            }
            if (step < 4) {
                await saveProgress(step + 1);
                setStep(step + 1);
            } else {
                await saveProgress(4, true);
                toast.success('¡Listo! Tu aerolínea está configurada');
                navigate('/dashboard');
            }
            load();
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === 'string' ? d : 'No se pudo guardar este paso');
        } finally {
            setSaving(false);
        }
    };

    const skip = async () => {
        try { await saveProgress(step); } catch { /* se reintenta luego */ }
        navigate('/dashboard');
    };

    if (error) return <div className="max-w-xl mx-auto py-20 text-center" data-testid="onboarding-error"><p className="text-red-400 mb-4">{error}</p><Button onClick={load}>Reintentar</Button></div>;
    if (!state) return <div className="flex justify-center py-24" data-testid="onboarding-loading"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>;
    const c = state.counts;

    return (
        <div className="max-w-5xl mx-auto py-6" data-testid="onboarding-page">
            <p className="text-xs uppercase tracking-[0.3em] text-amber-500 mb-2">Configuración inicial</p>
            <h1 className="text-4xl sm:text-5xl font-bold uppercase font-['Barlow_Condensed'] mb-2">Pon tu aerolínea en marcha</h1>
            <p className="text-muted-foreground mb-8">Cuatro pasos. Puedes completarlos ahora o volver cuando quieras.</p>
            <Stepper step={step} setStep={setStep} />

            <section className="rounded-2xl border border-border bg-card p-6 md:p-8 min-h-[320px]">
                {step === 1 && <AirlineStep airline={airline} setAirline={setAirline} />}
                {step === 2 && (<>
                    <p className="text-sm text-muted-foreground mb-4">Usuarios activos: <strong className="text-foreground" data-testid="count-users">{c.users}</strong></p>
                    <TeamInviteStep onInvited={load} />
                </>)}
                {step === 3 && (
                    <div className="grid md:grid-cols-2 gap-4">
                        <CsvImportPanel kind="aircraft" title={`Flota (${c.aircraft} aeronaves)`} hint="Matrícula, tipo, fabricante, modelo, serie, año, asientos y base." onImported={load} />
                        <CsvImportPanel kind="routes" title={`Rutas (${c.routes} programadas)`} hint="Número de vuelo, origen, destino, horas, días (1=lunes…7=domingo) y vigencia." onImported={load} />
                    </div>
                )}
                {step === 4 && (state.crew_module ? (
                    <CsvImportPanel kind="crew" title={`Tripulación (${c.crew} tripulantes)`} hint="Nombre, correo, puesto (captain, first_officer, flight_attendant), licencias, base y habilitaciones separadas por ;" onImported={load} />
                ) : (
                    <div className="text-center py-10" data-testid="crew-step-locked">
                        <Lock className="w-8 h-8 text-amber-500 mx-auto mb-3" />
                        <p className="mb-4 text-muted-foreground">La carga de tripulación requiere el módulo Tripulaciones.</p>
                        <ContractModuleButton moduleKey="crew" size="default" />
                    </div>
                ))}
            </section>

            <div className="flex flex-wrap justify-between gap-3 mt-6">
                <Button variant="ghost" onClick={skip} data-testid="onboarding-skip">Terminar después</Button>
                <div className="flex gap-2">
                    {step > 1 && <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="onboarding-back">Atrás</Button>}
                    <Button onClick={goNext} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black font-bold" data-testid="onboarding-next">
                        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{step === 1 ? 'Guardar y continuar' : step < 4 ? 'Continuar' : 'Finalizar'}
                    </Button>
                </div>
            </div>
        </div>
    );
}
